import type { CreateVisitRequestBody } from "@/common/types/visits";
import { createVisit } from "@/services/visits";
import { useBackgroundVisitUploadStore } from "@/store/backgroundVisitUpload.store";
import { usePostOnboardingStore } from "@/store/postOnboarding.store";
import { useState } from "react";

export const useVisitSubmit = (): {
  submitVisit: (body: CreateVisitRequestBody, audioBlob: Blob) => Promise<void>;
  submitVisitWithoutFile: (body: CreateVisitRequestBody) => Promise<void>;
  result: boolean;
  loading: boolean;
  error: string | null;
} => {
  const [result, setResult] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { enqueueVisitToUpload } = useBackgroundVisitUploadStore.getState();

  const submitVisit = async (
    body: CreateVisitRequestBody,
    audioBlob: Blob
  ): Promise<void> => {
    setResult(false);
    setLoading(true);
    setError(null);
    try {
      const result = await createVisit(body);
      setResult(true);
      usePostOnboardingStore.getState().markDone();
      void enqueueVisitToUpload({
        fileId: result.fileId,
        file: audioBlob,
        userId: Number(body.user),
        duration: body.duration,
        language: body.language,
        style: body.style,
        format: body.format,
        exam: body.exam,
        template: body.template,
        macros: "",
        customTemplateId: body.customTemplate || null,
      });
    } catch (error) {
      setError(error as string);
    } finally {
      setLoading(false);
    }
  };

  const submitVisitWithoutFile = async (
    body: CreateVisitRequestBody
  ): Promise<void> => {
    setLoading(true);
    setResult(false);
    setError(null);
    try {
      await createVisit(body);
      setResult(true);
      usePostOnboardingStore.getState().markDone();
    } catch (error) {
      setError(error as string);
    } finally {
      setLoading(false);
    }
  };

  return {
    submitVisit,
    result,
    loading,
    error,
    submitVisitWithoutFile,
  };
};
